import { useBulkCheckout } from "@apis/bulkCheckout";
import { Button, Spacer } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { processAndFormatSubSections } from "@components/molecules/BMH/utils";
import DynamicForm from "@components/molecules/DynamicForm/DynamicForm";
import { usePdpLayout } from "@redux/slices/pdpLayoutSlice";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { getStockItemType } from "@scenes/searchResults/Utils";
import { ProductCategory } from "@utils/constants";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { css } from "styled-components/native";

const ReviewPageRequest = ({
  productType,
  priceMode,
  setRequestData,
  toggleModal,
}: any) => {
  const pdpLayout = usePdpLayout();
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    itemDetail: mItemDetail,
    stoneItemDetail: mStoneItemDetail,
    reviewTotalPrice,
    personalization,
    matchingPairItemDetail,
    jcSelectedOption,
  } = useJewelryConfiguratorState();
  const itemDetail = useMemo(
    () =>
      mItemDetail?.type !== ProductCategory.JEWELRY &&
      mItemDetail?.type !== ProductCategory.LABGROWN_JEWELRY
        ? mStoneItemDetail
        : mItemDetail,
    [mItemDetail?.type]
  );
  const stoneItemDetail = useMemo(
    () =>
      mStoneItemDetail?.type === ProductCategory.JEWELRY ||
      mStoneItemDetail?.type === ProductCategory.LABGROWN_JEWELRY
        ? mItemDetail
        : mStoneItemDetail,
    [mStoneItemDetail?.type]
  );
  const formInputs = processAndFormatSubSections(
    pdpLayout[productType]?.review_page?.custom_bmh,
    "buy",
    priceMode
  );

  const { dynamicFormValues, isDynamicFormValid } = useDynamicFormState();

  const bulkCheckout = useBulkCheckout();
  const params = {
    bulk_request: {
      additional_attributes: {
        jewelry_builder: {
          config: jcSelectedOption.current.option_value || {},
          personalization: personalization || {},
          selected_primary_jewelry_id: itemDetail.id,
          selected_jewelry_id: itemDetail.id,
          selected_stock_item_ids: [
            stoneItemDetail.id,
            ...(Object.keys(matchingPairItemDetail || {}).length > 0
              ? [matchingPairItemDetail.id]
              : []),
          ],
        },
      },
      comments: dynamicFormValues?.requestForm?.comments || "",
      offer_total_sales_price: reviewTotalPrice,
      pay_within: 30,
      payment_type: "Invoice",
      price_mode: priceMode,
      request_type: "buy",
      secondary_request_type: "jewelry_builder",
      stock_item_request: [
        {
          offer_price_per_carat: itemDetail.price_per_carat ?? null,
          offer_total_sales_price: itemDetail.total_sales_price ?? null,
          stock_item_id: itemDetail.id,
          stock_item_type: getStockItemType(itemDetail?.type),
          config: jcSelectedOption.current.option_value || {},
          personalization: personalization || {},
        },
        {
          offer_discount_percent: stoneItemDetail.discount_percent ?? null,
          offer_price_per_carat: stoneItemDetail.price_per_carat ?? null,
          offer_total_sales_price: stoneItemDetail.total_sales_price ?? null,
          stock_item_id: stoneItemDetail.id,
          stock_item_type: getStockItemType(stoneItemDetail?.type),
        },
        ...(Object.keys(matchingPairItemDetail || {}).length > 0
          ? [
              {
                offer_discount_percent:
                  matchingPairItemDetail.discount_percent ?? null,
                offer_price_per_carat:
                  matchingPairItemDetail.price_per_carat ?? null,
                offer_total_sales_price:
                  matchingPairItemDetail.total_sales_price ?? null,
                stock_item_id: matchingPairItemDetail.id,
                stock_item_type: getStockItemType(matchingPairItemDetail?.type),
              },
            ]
          : []),
      ],
      stock_item_type: "Jewelry",
    },
  };
  const placeRequest = () => {
    bulkCheckout.mutate(params, {
      onSuccess(data: any) {
        setRequestData(data.data.response.stock_item_request);
        toggleModal("showBMHModal", false);
        toggleModal("showRequestSuccessPopUp", true);
      },
    });
  };

  return (
    <>
      <ScrollView
        keyboardShouldPersistTaps={"handled"}
        automaticallyAdjustKeyboardInsets={true}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <ParentContainer>
          <StyledContainer>
            <DynamicForm
              form={formInputs}
              fromRequestDetail={false}
              hideSubmitButton={true}
            />
          </StyledContainer>
        </ParentContainer>
        <Spacer size={SpaceType.Basic} />
      </ScrollView>
      <ButtonContainer theme={theme}>
        <ContainerOverlay>
          <Button
            title={t("reviewPage.request.popUp.button.text")}
            category={ButtonCategory.jewelry}
            type={ButtonType.primary}
            onPress={() => placeRequest()}
            disabled={bulkCheckout.isLoading || isDynamicFormValid?.isValid}
            loading={bulkCheckout.isLoading}
          />
        </ContainerOverlay>
      </ButtonContainer>
    </>
  );
};

const ParentContainer = styled.View`
  align-items: center;
`;

const StyledContainer = styled.View`
  ${({ theme }) =>
    !theme.isLargeScreen &&
    css`
      padding-top: 20px;
      padding-left: ${SpaceType.Double}px;
      padding-right: ${SpaceType.Double}px;
    `};
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      padding-top: ${SpaceType.Basic}px;
      padding-horizontal: ${SpaceType.Basic}px;
    `};
  max-width: 750px;
  width: 100%;
`;

const ButtonContainer = styled.View<{
  itemType?: any;
  theme?: any;
}>`
  padding-top: ${({ theme }) =>
    theme.isLargeScreen ? `${SpaceType.Double}px` : `${SpaceType.Big}px`};
  padding-bottom: ${({ theme }) =>
    theme.isLargeScreen
      ? `${SpaceType.ExtraSmall}px`
      : `${SpaceType.ExtraLarge}px`};
  background-color: ${({ theme }) => theme["core-01"]};
  padding-horizontal: ${({ theme }) =>
    theme.isLargeScreen
      ? `${SpaceType.ExtraSmall}px`
      : `${SpaceType.Double}px`};
  align-items: center;
  ${({ theme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default ReviewPageRequest;
